import 'bootstrap/dist/css/bootstrap.css';
// import { getOriginadorFromUrl } from '../../../../utils/urlUtils'
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import ReactInputMask from 'react-input-mask';
import DateInput from '../../../shared/dateInput';
import Spinner from '../../../shared/spinner';
import CustomAlert from '../../../shared/customAlert';
import Ph3aService, { DataCheckupRequest, SearchRequest, SpiderRequest } from '../../../services/ph3aService';
import DynamicResultTable from './DynamicResultTable';
import DataResultDisplay from './DataResultDisplay';
import CheckupResultDisplay from './CheckupResultDisplay';

type Endpoint = 'data' | 'checkup' | 'search' | 'requestId' | 'receita' | 'processos';

function ConsultaPH3AComponent() {

    const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint>('data');
    
    const [formData, setFormData] = useState<any>({
        document: '',
        dataNascimento: '',
        emails: [{ email: '' }],
        phones: [],
        addresses: [],
        name: '',
        city: '',
        street: '',
        zipCode: '',
        addressNumber: '',
        radius: '',
        size: 10,
        requestId: ''
    });

    const [resultado, setResultado] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);

    const ID_PROMOTORA = 1;

    const ph3aService = new Ph3aService();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleListInputChange = (listName: string, index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const list = [...formData[listName]];
        list[index][name] = value;
        setFormData({ ...formData, [listName]: list });
    };

    const addListItem = (listName: string) => {
        const list = formData[listName] || [];
        let newItem = {};
        if (listName === 'emails') newItem = { email: '' };
        if (listName === 'phones') newItem = { area: '', number: '' };
        if (listName === 'addresses') newItem = { street: '', number: '', city: '', state: '', zipCode: '' };
        setFormData({ ...formData, [listName]: [...list, newItem] });
    };

    const removeListItem = (listName: string, index: number) => {
        const list = [...formData[listName]];
        list.splice(index, 1);
        setFormData({ ...formData, [listName]: list });
    };

    const handleEndpointChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const endpoint = e.target.value as Endpoint;
        setSelectedEndpoint(endpoint);
        
        if (endpoint === 'checkup') {
            setFormData({ document: '', emails: [{ email: '' }], phones: [], addresses: [] });
        } else {
            setFormData({ document: '', dataNascimento: '', requestId: '', size: 10 }); 
        }
        setResultado(null);
        setAlert(null);
    };

    const handleSearch = async () => {
        setLoading(true);
        setResultado(null);
        setAlert(null);

        try {
            let response: any;
            const docClean = formData.document ? formData.document.replace(/\D/g, '') : '';

            switch (selectedEndpoint) {
                case 'data':
                    if (!docClean) { throw new Error("O campo Documento (CPF/CNPJ) é obrigatório."); }
                    response = await ph3aService.consultarDados(ID_PROMOTORA, { Document: docClean });
                    break;

                case 'checkup': {
                    if (!docClean) { throw new Error("O campo Documento (CPF/CNPJ) é obrigatório."); }
                    const emails = formData.emails?.filter((e: any) => e.email) || [];
                    const phones = formData.phones?.filter((p: any) => p.area && p.number) || [];
                    const addresses = formData.addresses?.filter((a: any) => a.street || a.city) || [];

                    if (emails.length === 0 && phones.length === 0 && addresses.length === 0) {
                        throw new Error("Forneça ao menos um E-mail, Telefone ou Endereço para checagem.");
                    }

                    const checkupPayload: DataCheckupRequest = {
                        Document: docClean,
                        Email: emails,
                        Phones: phones.map((p: any) => ({ ...p, Area: Number(p.area) })),
                        Address: addresses,
                    };
                    response = await ph3aService.realizarCheckup(ID_PROMOTORA, checkupPayload);
                    break;
                }

                case 'search':
                    if (!formData.name) {
                        throw new Error("O campo Nome é obrigatório para a Busca Avançada.");
                    }
                    const searchPayload: SearchRequest = {
                        Name: formData.name,
                        City: formData.city,
                        Street: formData.street,
                        ZipCode: formData.zipCode ? formData.zipCode.replace(/\D/g, '') : undefined,
                        AddressNumber: formData.addressNumber,
                        Radius: formData.radius ? Number(formData.radius) : undefined,
                    };
                    response = await ph3aService.buscarPorCriterios(ID_PROMOTORA, 1, Number(formData.size) || 10, searchPayload);
                    break;

                case 'requestId':
                    if (!formData.requestId) { throw new Error("O campo Request ID é obrigatório."); }
                    response = await ph3aService.consultarPorRequestId(ID_PROMOTORA, formData.requestId);
                    break;

                case 'receita': {
                    if (!docClean) { throw new Error("O campo CPF é obrigatório."); }
                    if (!formData.birthDate || formData.birthDate.length < 10) { 
                        throw new Error("O campo Data de Nascimento é obrigatório."); 
                    }

                    const spiderPayloadReceita: SpiderRequest = {
                        input: { 
                            cpf: docClean,
                            dataNascimento: formData.dataNascimento,
                            apiType_In: null as any
                        }
                    };

                    const triggerResponseReceita = await ph3aService.dispararSpider(ID_PROMOTORA, spiderPayloadReceita);

                    if (!triggerResponseReceita || !triggerResponseReceita.requestId) {
                        throw new Error("Falha ao iniciar consulta na Receita: RequestId não retornado.");
                    }

                    const resultReceita = await ph3aService.obterResultadoReceita(ID_PROMOTORA, triggerResponseReceita.requestId);
                    
                    response = resultReceita.data; 
                    break;
                }

                case 'processos': {
                    if (!docClean) { throw new Error("O campo Documento (CPF/CNPJ) é obrigatório."); }

                    const spiderPayloadProcessos: SpiderRequest = {
                        input: { 
                            cpfCnpj_In: docClean,
                            apiType_In: '2' 
                        }
                    };

                    const triggerResponseProcessos = await ph3aService.dispararSpider(ID_PROMOTORA, spiderPayloadProcessos);

                    if (!triggerResponseProcessos || !triggerResponseProcessos.requestId) {
                        throw new Error("Falha ao iniciar consulta de Processos: RequestId não retornado.");
                    }

                    const resultProcessos = await ph3aService.obterResultadoProcessos(ID_PROMOTORA, triggerResponseProcessos.requestId);

                    response = resultProcessos.data;
                    break;
                }
            }

            setResultado(response);
            setAlert({ message: 'Consulta realizada com sucesso!', type: 'success' });
        } catch (error) {
            const errorMessage = (error as any).response?.data?.error || (error as Error).message || 'Ocorreu um erro desconhecido.';
            setAlert({ message: `Erro na consulta: ${errorMessage}`, type: 'error' });
            console.error('Erro na consulta:', error);
        } finally {
            setLoading(false);
        }
    };

    const renderFormFields = () => {
        const docValue = formData.document || '';
        const maskData = docValue.replace(/\D/g, '').length > 11 ? '99.999.999/9999-99' : '999.999.999-99';

        switch (selectedEndpoint) {
            case 'data':
                return (
                    <div className="col-md-4">
                        <label htmlFor="document" className="form-label">Documento (CPF/CNPJ) <span className="text-danger">*</span></label>
                        <ReactInputMask
                            id="document"
                            name="document"
                            className="form-control"
                            mask={maskData}
                            placeholder="Digite o CPF ou CNPJ"
                            value={docValue}
                            onChange={handleInputChange}
                        />
                    </div>
                );
            case 'receita':
                return (
                    <>
                        <div className="col-md-4">
                            <label htmlFor="document" className="form-label">CPF (Receita Federal) <span className="text-danger">*</span></label>
                            <ReactInputMask
                                id="document"
                                name="document"
                                className="form-control"
                                mask="999.999.999-99"
                                placeholder="Digite o CPF"
                                value={docValue}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="col-md-3">
                            <label htmlFor="birthDate" className="form-label">Data de Nascimento <span className="text-danger">*</span></label>
                            <DateInput
                                id="birthDate"
                                name="birthDate"
                                className="form-control"
                                placeholder="DD/MM/AAAA"
                                value={formData.birthDate || ''}
                                onChange={handleInputChange}
                            />
                        </div>
                    </>
                );
            case 'processos':
                return (
                    <div className="col-md-4">
                        <label htmlFor="document" className="form-label">Documento (Processos) <span className="text-danger">*</span></label>
                        <ReactInputMask
                            id="document"
                            name="document"
                            className="form-control"
                            mask={maskData}
                            placeholder="Digite o CPF ou CNPJ"
                            value={docValue}
                            onChange={handleInputChange}
                        />
                    </div>
                );
            case 'checkup':
                return (
                    <div className="col-12">
                        <div className="row g-3">
                            <div className="col-md-12">
                                <label className="form-label">Documento (CPF/CNPJ) <span className="text-danger">*</span></label>
                                <ReactInputMask id="document" name="document" className="form-control" mask={maskData} value={docValue} onChange={handleInputChange}/>
                            </div>

                            <div className="col-md-12">
                                <label className="form-label">Emails para Checagem</label>
                                {formData.emails?.map((email: any, index: number) => (
                                    <div key={index} className="input-group mb-2">
                                        <input type="email" name="email" className="form-control" placeholder="exemplo@email.com" value={email.email} onChange={(e) => handleListInputChange('emails', index, e)} />
                                        <button className="btn btn-outline-danger" type="button" onClick={() => removeListItem('emails', index)}><FontAwesomeIcon icon={faTrash} /></button>
                                    </div>
                                ))}
                                <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => addListItem('emails')}><FontAwesomeIcon icon={faPlus} /> Adicionar Email</button>
                            </div>

                            <div className="col-md-12">
                                <label className="form-label mt-2">Telefones para Checagem</label>
                                {formData.phones?.map((phone: any, index: number) => (
                                    <div key={index} className="input-group mb-2">
                                        <span className="input-group-text">DDD</span>
                                        <input type="text" name="area" className="form-control" placeholder="61" value={phone.area} onChange={(e) => handleListInputChange('phones', index, e)} style={{maxWidth: '80px'}}/>
                                        <span className="input-group-text">Número</span>
                                        <input type="text" name="number" className="form-control" placeholder="999999999" value={phone.number} onChange={(e) => handleListInputChange('phones', index, e)} />
                                        <button className="btn btn-outline-danger" type="button" onClick={() => removeListItem('phones', index)}><FontAwesomeIcon icon={faTrash} /></button>
                                    </div>
                                ))}
                                <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => addListItem('phones')}><FontAwesomeIcon icon={faPlus} /> Adicionar Telefone</button>
                            </div>
                            
                            <div className="col-md-12">
                                <label className="form-label mt-2">Endereços para Checagem</label>
                                {formData.addresses?.map((addr: any, index: number) => (
                                    <div key={index} className="border p-2 rounded mb-2">
                                        <div className="row g-2">
                                            <div className="col-12 d-flex justify-content-end">
                                                <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => removeListItem('addresses', index)}>Remover Endereço</button>
                                            </div>
                                            <div className="col-md-6"><label className="form-label small">Rua</label><input type="text" name="street" className="form-control form-control-sm" value={addr.street || ''} onChange={(e) => handleListInputChange('addresses', index, e)} /></div>
                                            <div className="col-md-2"><label className="form-label small">Número</label><input type="text" name="number" className="form-control form-control-sm" value={addr.number || ''} onChange={(e) => handleListInputChange('addresses', index, e)} /></div>
                                            <div className="col-md-4"><label className="form-label small">Cidade</label><input type="text" name="city" className="form-control form-control-sm" value={addr.city || ''} onChange={(e) => handleListInputChange('addresses', index, e)} /></div>
                                        </div>
                                    </div>
                                ))}
                                <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => addListItem('addresses')}><FontAwesomeIcon icon={faPlus} /> Adicionar Endereço</button>
                            </div>
                        </div>
                    </div>
                );
            case 'search':
                return (
                    <>
                        <div className="col-md-4">
                            <label className="form-label">Nome <span className="text-danger">*</span></label>
                            <input type="text" name="name" className="form-control" value={formData.name || ''} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Cidade</label>
                            <input type="text" name="city" className="form-control" value={formData.city || ''} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Bairro</label>
                            <input type="text" name="street" className="form-control" value={formData.street || ''} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">CEP</label>
                            <ReactInputMask name="zipCode" className="form-control" mask="99999-999" placeholder="_____-___" value={formData.zipCode || ''} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Número da Residência</label>
                            <input type="text" name="addressNumber" className="form-control" value={formData.addressNumber || ''} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-1">
                            <label className="form-label">Radius</label>
                            <input type="number" name="radius" className="form-control" placeholder="1" value={formData.radius || ''} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Quantidade</label>
                            <input type="number" name="size" className="form-control" value={formData.size || '10'} onChange={handleInputChange} />
                        </div>
                    </>
                );
            case 'requestId':
                return (
                    <div className="col-md-4">
                        <label htmlFor="requestId" className="form-label">Request ID <span className="text-danger">*</span></label>
                        <input type="text" id="requestId" name="requestId" className="form-control" value={formData.requestId || ''} onChange={handleInputChange} />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
            <div className="container-fluid mt-3">
                <div className="card">
                    <div className="card-header bg-light"><h5 className="mb-0">Consulta PH3A</h5></div>
                    <div className="card-body">
                        <div className="row g-3 mb-3">
                            <div className="col-md-4">
                                <label htmlFor="endpoint-selector" className="form-label">Selecione o Tipo de Consulta</label>
                                <select id="endpoint-selector" className="form-select" value={selectedEndpoint} onChange={handleEndpointChange}>
                                    <option value="data">Busca por Documento</option>
                                    <option value="receita">Consulta Receita Federal</option>
                                    <option value="processos">Consulta Processos Jurídicos</option>
                                    {/* <option value="checkup">Checkup de Dados</option> */}
                                    <option value="search">Busca Avançada</option>
                                    <option value="requestId">Busca por Request ID</option>
                                </select>
                            </div>
                        </div>

                        <div className="row g-3 align-items-end mb-3">
                            {renderFormFields()}
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                                    {loading ? 'Consultando...' : 'Buscar'}
                                </button>
                            </div>
                        </div>

                        {loading && <Spinner loading={loading} />}
                        {!loading && resultado && (
                            <>
                                {(selectedEndpoint === 'data' || selectedEndpoint === 'requestId') && <DataResultDisplay data={resultado} />}
                                {selectedEndpoint === 'checkup' && <CheckupResultDisplay data={resultado} />}
                                {(selectedEndpoint !== 'data' && selectedEndpoint !== 'requestId' && selectedEndpoint !== 'checkup') && <DynamicResultTable data={resultado} />}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ConsultaPH3AComponent;